import { Component, Input } from '@angular/core';
import { CommonService } from 'src/app/core/services/common.service';

export interface BlacklistedNGORecord extends Record<string, unknown> {
  address: string;
  debarringDate: string;
  links: string;
  matchConfidence: string;
  name: string;
  nameCeoAndMd: string;
  portal: string;
  registrationNoAndDate: string;
  similarityScore: number;
  source: string;
}

interface RiskLevel {
  label: string;
  badgeClass: string;
  indicatorClass: string;
  description: string;
}

@Component({
  selector: 'app-blacklist-exposure-card',
  templateUrl: './blacklist-exposure-card.component.html',
  styleUrls: ['./blacklist-exposure-card.component.scss']
})
export class BlacklistExposureCardComponent {
  @Input() matches: BlacklistedNGORecord[] = [];
  @Input() syncDate?: string;

  constructor(private commonService: CommonService) { }

  get sortedMatches(): BlacklistedNGORecord[] {
    return [...this.matches].sort((a, b) => (b.similarityScore ?? 0) - (a.similarityScore ?? 0));
  }

  get risk(): RiskLevel {
    return this.determineRiskLevel(this.sortedMatches);
  }

  get sourcesScanned(): number {
    const sources = new Set(
      this.sortedMatches.map(match => match['source'] || match['portal']).filter(Boolean)
    );
    return sources.size;
  }

  get formattedSyncDate(): string {
    if (!this.syncDate) {
      return '';
    }
    return this.formatDate(this.syncDate);
  }

  get topMatches(): BlacklistedNGORecord[] {
    return this.sortedMatches.slice(0, 3);
  }


  formatDate(date: string): string {
    if (!date) return 'N/A';
    try {
      const dateObj = new Date(date);
      return this.commonService.dateFormatter(dateObj.toISOString());
    } catch {
      return "N/A";
    }
  }

  formatConfidenceLabel(matchConfidence?: string): string {
    if (!matchConfidence) {
      return 'Unknown';
    }
    return matchConfidence
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  determineRiskLevel(matches: BlacklistedNGORecord[]): RiskLevel {
    if (!matches.length) {
      return {
        label: 'No Matches Detected',
        badgeClass: 'bg-green-100 text-green-800',
        indicatorClass: 'bg-green-500',
        description: 'No blacklisting signals found across scanned registries.'
      };
    }

    const [topMatch] = matches;
    const score = topMatch?.similarityScore ?? 0;
    const confidence = topMatch?.matchConfidence?.toLowerCase() ?? '';

    if (score >= 0.9 || confidence.includes('very')) {
      return {
        label: 'Critical Risk',
        badgeClass: 'bg-red-100 text-red-800',
        indicatorClass: 'bg-red-500',
        description: 'Entity appears on one or more blacklists with very high confidence. Immediate escalation recommended.'
      };
    }

    if (score >= 0.75 || confidence.includes('high')) {
      return {
        label: 'High Risk',
        badgeClass: 'bg-orange-100 text-orange-800',
        indicatorClass: 'bg-orange-500',
        description: 'Confirmed matches detected. Review blacklist context and enforce enhanced due diligence.'
      };
    }

    return {
      label: 'Medium Risk',
      badgeClass: 'bg-yellow-100 text-yellow-800',
      indicatorClass: 'bg-yellow-500',
      description: 'Potential matches identified. Validate underlying source records before onboarding decisions.'
    };
  }
}

